import { useEffect, useRef, useState, useCallback } from "react";
import { useTranslation } from "react-i18next";
import axios from "axios";
import { postWithRetry, ChatRequestError } from "@/api/retry";
import { SkeletonCards, DailyPickBanner, WatchStrip, RecentChips } from "@/components/EngagementSurfaces";
import { rememberQuery, rememberHome } from "@/lib/engagement";
import { Toaster, toast } from "sonner";
import { PanelLeftOpen, Share2, X as XIcon } from "lucide-react";
import ChatComposer from "@/components/chat/ChatComposer";
import MessageBubble from "@/components/chat/MessageBubble";
import FlightGroupCard from "@/components/cards/FlightGroupCard";
import HotelListCard from "@/components/cards/HotelListCard";
import PackageGroupCard from "@/components/cards/PackageGroupCard";
import MultiModalRouteCard from "@/components/cards/MultiModalRouteCard";
import HolidayPackageCard from "@/components/cards/HolidayPackageCard";
import WeatherCard from "@/components/cards/WeatherCard";
import ItineraryCard from "@/components/cards/ItineraryCard";
import MapCard from "@/components/cards/MapCard";
import EmptyResultCard from "@/components/cards/EmptyResultCard";
import ExperiencesGroupCard from "@/components/cards/ExperiencesGroupCard";
import DatePickerChipsCard from "@/components/cards/DatePickerChipsCard";
import CostSummaryCard from "@/components/cards/CostSummaryCard";
import ClarifyActionCard from "@/components/cards/ClarifyActionCard";
import AdCard from "@/components/cards/AdCard";
import CtaButtonsCard from "@/components/cards/CtaButtonsCard";
import LocalExperienceCard from "@/components/cards/LocalExperienceCard";
import HamburgerMenu from "@/components/HamburgerMenu";
import LanguageSwitcher from "@/components/LanguageSwitcher";
import DealsRail from "@/components/DealsRail";
import UserMenu from "@/components/UserMenu";
import ChatsSidebar from "@/components/ChatsSidebar";
import CurrencySwitcher from "@/components/CurrencySwitcher";
import NearMeChip from "@/components/NearMeChip";
import Logo from "@/components/Logo";
import AiDisclaimer from "@/components/AiDisclaimer";
import ShareSessionModal from "@/components/ShareSessionModal";
import PresenceAvatars from "@/components/PresenceAvatars";
import TypingDots from "@/components/TypingDots";
import DiscoverButton from "@/components/explorer/DiscoverButton";
import ExplorerModal from "@/components/explorer/ExplorerModal";
import { useGeo } from "@/context/GeoContext";
import { useAuth, authHeaders } from "@/context/AuthContext";
import { useRealtimeRoom } from "@/lib/realtime";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const SESSION_KEY = "travelchat_session_id";

// Phase 14.8 — `safeStorage` mirrors the Web Storage API but fails
// closed in incognito modes where `localStorage` throws SecurityError.
// Without this shim, the first useState initializer below crashes the
// entire React tree → "flash then blank white" reported in incognito.
import safeStorage from "@/lib/safeStorage";

function newSessionId() {
    return typeof crypto !== "undefined" && crypto.randomUUID
        ? crypto.randomUUID()
        : `s_${Math.random().toString(36).slice(2)}_${Date.now()}`;
}

function getOrCreateSessionId() {
    let id = safeStorage.getItem(SESSION_KEY);
    if (!id) {
        id = newSessionId();
        safeStorage.setItem(SESSION_KEY, id);
    }
    return id;
}

function renderCard(card, key, onPlacesToSee) {
    if (card.type === "flight_group")
        return <FlightGroupCard key={key} card={card} onPlacesToSee={onPlacesToSee} />;
    if (card.type === "hotel_list")
        return <HotelListCard key={key} card={card} onPlacesToSee={onPlacesToSee} />;
    if (card.type === "package_group")
        return <PackageGroupCard key={key} card={card} onPlacesToSee={onPlacesToSee} />;
    // Phase 9 — non-airport destination card types.
    if (card.type === "multi_modal_route" || card.card_type === "multi_modal_route")
        return <MultiModalRouteCard key={key} card={card} />;
    if (card.type === "holiday_package" || card.card_type === "holiday_package")
        return <HolidayPackageCard key={key} card={card} />;
    if (card.type === "experiences_group")
        return <ExperiencesGroupCard key={key} card={card} />;
    if (card.type === "weather") return <WeatherCard key={key} card={card} />;
    if (card.type === "itinerary") return <ItineraryCard key={key} card={card} onSend={onPlacesToSee} />;
    if (card.type === "map") return <MapCard key={key} card={card} />;
    if (card.type === "empty_result")
        return <EmptyResultCard key={key} card={card} onPlacesToSee={onPlacesToSee} />;
    if (card.type === "date_picker_chips")
        return <DatePickerChipsCard key={key} card={card} onPick={onPlacesToSee} />;
    if (card.type === "cost_summary")
        return <CostSummaryCard key={key} card={card} />;
    if (card.type === "clarify_action")
        return <ClarifyActionCard key={key} card={card} onPick={onPlacesToSee} />;
    if (card.type === "ad") return <AdCard key={key} card={card} />;
    if (card.type === "local_experience")
        return <LocalExperienceCard key={key} card={card} />;
    if (card.type === "cta_buttons")
        return <CtaButtonsCard key={key} card={card} onSend={onPlacesToSee} />;
    return null;
}

export default function HomePage() {
    const [sessionId, setSessionId] = useState(getOrCreateSessionId);
    const [messages, setMessages] = useState([]);
    const [sending, setSending] = useState(false);
    // Phase 14.9.15 A1 — default closed on mobile (<768 px) so the
    // 260 px sidebar doesn't cover half the iPhone Pro Max viewport
    // (430 px). Desktop keeps the previous "open by default" UX.
    const [sidebarOpen, setSidebarOpen] = useState(() => {
        if (typeof window === "undefined") return true;
        try {
            return window.matchMedia("(min-width: 768px)").matches;
        } catch {
            return true;
        }
    });
    const [sidebarRefresh, setSidebarRefresh] = useState(0);
    const [shareOpen, setShareOpen] = useState(false);
    const [shareState, setShareState] = useState({ token: null, permission: null });
    const [assistantReplyTick, setAssistantReplyTick] = useState(0);
    const [tipDismissed, setTipDismissed] = useState(
        () => safeStorage.getItem("tc_share_tip_dismissed") === "1",
    );
    const [explorerOpen, setExplorerOpen] = useState(false);
    const scrollRef = useRef(null);
    const { t } = useTranslation();

    const { geo, userGeo, currencyPref } = useGeo();
    const { user, token } = useAuth();

    // Reload messages whenever session_id changes (new chat or sidebar pick).
    useEffect(() => {
        let cancelled = false;
        async function load() {
            try {
                const url = token
                    ? `${API}/sessions/${sessionId}`
                    : `${API}/session/${sessionId}`;
                const res = await axios.get(url, {
                    headers: token ? authHeaders(token) : {},
                });
                if (!cancelled) {
                    setMessages(res.data?.messages || []);
                    setShareState({
                        token: res.data?.share_token || null,
                        permission: res.data?.share_permission || null,
                    });
                }
            } catch (e) {
                if (!cancelled) {
                    setMessages([]);
                    setShareState({ token: null, permission: null });
                }
                if (e?.response?.status && e.response.status !== 404) {
                    console.warn("[home] session load failed", e.response?.status);
                }
            }
        }
        load();
        return () => {
            cancelled = true;
        };
    }, [sessionId, token, user]);

    useEffect(() => {
        if (scrollRef.current) {
            scrollRef.current.scrollTo({
                top: scrollRef.current.scrollHeight,
                behavior: "smooth",
            });
        }
    }, [messages, sending]);

    // Refresh sidebar once user logs in (so adopted/anon session shows up after first authed send).
    useEffect(() => {
        if (user) setSidebarRefresh((r) => r + 1);
    }, [user]);

    const handleSend = useCallback(
        async (text) => {
            const trimmed = text.trim();
            if (!trimmed || sending) return;

            const now = new Date().toISOString();
            const userTurn = {
                id: `local-${Date.now()}`,
                role: "user",
                content: trimmed,
                cards: [],
                timestamp: now,
            };
            setMessages((prev) => [...prev, userTurn]);
            setSending(true);
            // Phase 16 — localStorage memory: recent destinations + home.
            try { rememberQuery(trimmed); rememberHome(userGeo?.nearest_iata, currencyPref || geo?.currency); } catch {}

            try {
                // Phase 6.7 debug aid — when the preview URL has
                // `?force_region=IN` (or similar), forward it as a query
                // param on /api/chat. The backend only honours it when
                // ENABLE_DEBUG_REGION_OVERRIDE=true (default off in prod).
                const forced = new URLSearchParams(window.location.search).get("force_region");
                const chatUrl = forced
                    ? `${API}/chat?force_region=${encodeURIComponent(forced)}`
                    : `${API}/chat`;
                const res = await postWithRetry(
                    chatUrl,
                    {
                        session_id: sessionId,
                        message: trimmed,
                        affiliate_region: geo?.affiliate_region || "WORLD",
                        currency_pref: currencyPref || geo?.currency || "USD",
                        user_geo: userGeo || null,
                    },
                    { headers: token ? authHeaders(token) : {} },
                );
                const assistantTurn = {
                    id: `local-a-${Date.now()}`,
                    role: "assistant",
                    content: res.data.reply_text || "",
                    cards: res.data.cards || [],
                    timestamp: new Date().toISOString(),
                };
                setMessages((prev) => [...prev, assistantTurn]);
                if (user) setSidebarRefresh((r) => r + 1);
            } catch (e) {
                const kind = e instanceof ChatRequestError ? e.kind : "network";
                console.error("[chat] failed after retries", kind, e);
                const msgKey =
                    kind === "rate_limited"
                        ? "chat.error_rate_limited"
                        : kind === "server_error"
                        ? "chat.error_server"
                        : kind === "timeout"
                        ? "chat.error_timeout"
                        : "chat.error_network";
                toast.error(t(msgKey), {
                    action: kind !== "rate_limited"
                        ? { label: t("common.retry"), onClick: () => handleSend(trimmed) }
                        : undefined,
                });
            } finally {
                setSending(false);
            }
        },
        [sending, sessionId, geo, userGeo, currencyPref, token, user],
    );

    const handleNewChat = useCallback(() => {
        const id = newSessionId();
        safeStorage.setItem(SESSION_KEY, id);
        setSessionId(id);
        setMessages([]);
    }, []);

    const handleSelectSession = useCallback((id) => {
        safeStorage.setItem(SESSION_KEY, id);
        setSessionId(id);
    }, []);

    // ── Real-time room — only active for shared sessions (to avoid spinning
    // up a socket for every anonymous visitor). Owners join with their JWT;
    // anon viewers on this page never happen (the /chat/share/:token route
    // handles those).
    const rtOnEvent = useCallback((event, payload) => {
        setMessages((prev) => {
            if (prev.some((m) => m.id === payload.message_id)) return prev;
            if (event === "message:user") {
                // Skip echo of our own just-sent optimistic turn.
                if (user && payload.author_id === user.id) return prev;
                return [
                    ...prev,
                    {
                        id: payload.message_id,
                        role: "user",
                        content: payload.text,
                        cards: [],
                        timestamp: payload.ts,
                        author_name: payload.author_name,
                    },
                ];
            }
            if (event === "message:assistant") {
                return [
                    ...prev,
                    {
                        id: payload.message_id,
                        role: "assistant",
                        content: payload.text,
                        cards: payload.cards || [],
                        timestamp: payload.ts,
                    },
                ];
            }
            return prev;
        });
    }, [user]);

    const rtOnRevoke = useCallback(() => {
        toast(t("share.revoked_toast"));
    }, [t]);

    const realtimeActive = Boolean(user && token && shareState.token);
    const { presence, typing, emitTyping } = useRealtimeRoom({
        sessionId: realtimeActive ? sessionId : null,
        token: realtimeActive ? token : null,
        onServerEvent: rtOnEvent,
        onRevoke: rtOnRevoke,
    });

    const typingOthers = typing.filter((x) => !user || x.user_id !== user.id);
    const typingLabel = typingOthers.length === 0
        ? null
        : typingOthers.length === 1
        ? t("share.typing_one", { name: typingOthers[0].name })
        : t("share.typing_many", { count: typingOthers.length });

    const isOwner = Boolean(user && messages.length > 0);
    const hasAssistantMsg = messages.some((m) => m.role === "assistant");
    const showShareTip = isOwner && hasAssistantMsg && !tipDismissed && !shareState.token;

    // Bump `assistantReplyTick` on each new assistant message so the
    // composer can re-arm conversation-mode listening. We track the
    // assistant-message count to be resilient to the WebSocket-driven
    // duplicate-id guard (which de-dupes echoes but still grows the array).
    const assistantCountRef = useRef(0);
    useEffect(() => {
        const c = messages.filter((m) => m.role === "assistant").length;
        if (c > assistantCountRef.current) {
            assistantCountRef.current = c;
            setAssistantReplyTick((x) => x + 1);
        }
    }, [messages]);

    const dismissShareTip = useCallback(() => {
        safeStorage.setItem("tc_share_tip_dismissed", "1");
        setTipDismissed(true);
    }, []);

    const isEmpty = messages.length === 0 && !sending;

    // Phase 13A — detect "itinerary intent" on the most recent user
    // message so the long-running planner (30–130s) gets calming staged
    // copy instead of a stale spinner.
    const itineraryIntent = (() => {
        if (!sending) return false;
        for (let i = messages.length - 1; i >= 0; i--) {
            const m = messages[i];
            if (m.role !== "user") continue;
            const t = (m.content || "").toLowerCase();
            return /\b(itinerary|plan(\s+a)?\s+(trip|my)|\bday(s)?\s+in\b|honeymoon|weekend\s+in|trip\s+to|family\s+trip)\b/.test(
                t,
            );
        }
        return false;
    })();
    const [itineraryStage, setItineraryStage] = useState(0);
    useEffect(() => {
        if (!itineraryIntent) {
            setItineraryStage(0);
            return undefined;
        }
        const t1 = setTimeout(() => setItineraryStage(1), 30000);
        const t2 = setTimeout(() => setItineraryStage(2), 60000);
        return () => {
            clearTimeout(t1);
            clearTimeout(t2);
        };
    }, [itineraryIntent]);
    const itineraryStageCopy = [
        "Planning your itinerary…",
        "Almost there — finalizing day-by-day details…",
        "Your detailed itinerary is being prepared…",
    ][itineraryStage];

    return (
        <div
            className="App flex h-screen bg-white text-[#0A0A0A]"
            data-testid="app-root"
        >
            <Toaster
                position="top-center"
                toastOptions={{
                    style: {
                        background: "#0A0A0A",
                        color: "#FFFFFF",
                        border: "1px solid #0A0A0A",
                        fontFamily: "Inter, sans-serif",
                    },
                }}
            />

            {/* Sidebar (logged-in only) */}
            <ChatsSidebar
                open={sidebarOpen}
                onToggle={() => setSidebarOpen((v) => !v)}
                activeSessionId={sessionId}
                onSelectSession={handleSelectSession}
                onNewChat={handleNewChat}
                refreshKey={sidebarRefresh}
            />

            {/* Main column */}
            <div className="flex flex-1 flex-col overflow-hidden">
                {/* Header */}
                <header
                    className="sticky top-0 z-10 border-b border-[#E5E5E5] bg-white/90 backdrop-blur"
                    data-testid="app-header"
                >
                    <div className="mx-auto flex w-full max-w-[1200px] items-center justify-between px-5 py-3">
                        <div className="flex items-center gap-2">
                            <HamburgerMenu />
                            {user && !sidebarOpen && (
                                <button
                                    type="button"
                                    onClick={() => setSidebarOpen(true)}
                                    aria-label="Open chats"
                                    data-testid="sidebar-open"
                                    className="flex h-9 w-9 items-center justify-center rounded-full text-[#0A0A0A] transition hover:bg-[#F5F5F5]"
                                >
                                    <PanelLeftOpen size={17} strokeWidth={1.75} />
                                </button>
                            )}
                            <Logo size="sm" />
                        </div>
                        <div className="flex items-center gap-3">
                            <span
                                className="hidden text-[12px] text-[#737373] sm:inline"
                                data-testid="geo-pill"
                            >
                                {geo
                                    ? `${geo.country_code} · ${currencyPref || geo.currency}`
                                    : "Anonymous session"}
                            </span>
                            {realtimeActive && presence.length > 0 && (
                                <PresenceAvatars members={presence} />
                            )}
                            {(() => {
                                // Phase 8L.4 — always render the Share
                                // button so the feature is discoverable.
                                // Disable + tooltip when the gate fails.
                                const canShare = isOwner && messages.length > 0;
                                let label = t("share.share_button_label");
                                if (!user) {
                                    label = t("share.disabled_signin_hint");
                                } else if (messages.length === 0) {
                                    label = t("share.disabled_empty_hint");
                                }
                                return (
                                    <button
                                        type="button"
                                        onClick={canShare ? () => setShareOpen(true) : undefined}
                                        disabled={!canShare}
                                        aria-label={label}
                                        aria-disabled={!canShare}
                                        data-testid="share-button"
                                        title={label}
                                        className={`flex h-9 w-9 items-center justify-center rounded-full border transition ${
                                            !canShare
                                                ? "cursor-not-allowed border-[#E5E5E5] text-[#0A0A0A] opacity-40"
                                                : shareState.token
                                                ? "border-[#0A0A0A] bg-[#0A0A0A] text-white hover:opacity-90"
                                                : "border-[#E5E5E5] text-[#0A0A0A] hover:border-[#0A0A0A] hover:bg-[#F5F5F5]"
                                        }`}
                                    >
                                        <Share2 size={15} strokeWidth={1.9} />
                                    </button>
                                );
                            })()}
                            <CurrencySwitcher />
                            <LanguageSwitcher />
                            <UserMenu />
                        </div>
                    </div>
                </header>

                {isEmpty ? (
                    <EmptyHero
                        onSend={handleSend}
                        sending={sending}
                        assistantReplyTick={assistantReplyTick}
                    />
                ) : (
                    <>
                        {showShareTip && (
                            <div
                                className="mx-auto mt-3 flex w-full max-w-[820px] items-start gap-2 px-5"
                                data-testid="share-first-time-tip"
                            >
                                <div className="flex w-full items-start justify-between gap-2 rounded-xl border border-[#E5E5E5] bg-[#FAFAFA] px-3.5 py-2 text-[12.5px] text-[#0A0A0A]">
                                    <span>{t("share.first_time_tip")}</span>
                                    <button
                                        type="button"
                                        onClick={dismissShareTip}
                                        aria-label={t("share.first_time_tip_dismiss")}
                                        data-testid="share-tip-dismiss"
                                        className="inline-flex h-5 w-5 items-center justify-center rounded-full text-[#737373] transition hover:bg-white hover:text-[#0A0A0A]"
                                    >
                                        <XIcon size={12} />
                                    </button>
                                </div>
                            </div>
                        )}
                        <main
                            ref={scrollRef}
                            className="scroll-area flex-1 overflow-y-auto"
                            data-testid="conversation-scroll"
                        >
                            <div className="mx-auto w-full max-w-[820px] px-5 py-10">
                                <div className="space-y-8">
                                    {messages.map((m) => (
                                        <div
                                            key={m.id}
                                            className="fade-up"
                                            data-testid={`message-${m.role}`}
                                        >
                                            <MessageBubble
                                                role={m.role}
                                                content={m.content}
                                                author={m.author_name}
                                            />
                                            {m.role === "assistant" && m.cards && m.cards.length > 0 && (
                                                <div className="mt-4 space-y-4">
                                                    {m.cards.map((c, idx) =>
                                                        renderCard(c, `${m.id}-card-${idx}`, handleSend),
                                                    )}
                                                </div>
                                            )}
                                        </div>
                                    ))}
                                    {sending && (
                                        <div className="fade-up" data-testid="assistant-thinking">
                                            <MessageBubble role="assistant" content="" thinking />
                                            <div className="mt-3">
                                                <SkeletonCards count={2} />
                                            </div>
                                            {itineraryIntent && (
                                                <p
                                                    className="ml-1 mt-2 text-[12px] italic text-[#737373]"
                                                    data-testid="itinerary-loading-stage"
                                                    data-stage={itineraryStage}
                                                >
                                                    {itineraryStageCopy}
                                                </p>
                                            )}
                                        </div>
                                    )}
                                    {typingLabel && (
                                        <p
                                            className="flex items-center gap-2 text-[12px] italic text-[#737373]"
                                            data-testid="typing-indicator"
                                        >
                                            <span>✏️ {typingLabel}</span>
                                            <TypingDots />
                                        </p>
                                    )}
                                </div>
                            </div>
                        </main>

                        <footer
                            className="sticky bottom-0 z-10 border-t border-[#E5E5E5] bg-white"
                            data-testid="composer-footer"
                        >
                            <div className="mx-auto w-full max-w-[820px] px-5 py-4">
                                <NearMeChip />
                                <ChatComposer
                                    disabled={sending}
                                    onSend={handleSend}
                                    onType={realtimeActive ? emitTyping : undefined}
                                    assistantReplyTick={assistantReplyTick}
                                />
                                <AiDisclaimer />
                            </div>
                        </footer>
                    </>
                )}
            </div>

            <ShareSessionModal
                open={shareOpen}
                onOpenChange={setShareOpen}
                sessionId={sessionId}
                token={token}
                onShareChange={setShareState}
            />

            {/* Phase 7B — floating Discover ("PIQ") button + Explorer modal */}
            <DiscoverButton onOpen={() => setExplorerOpen(true)} />
            <ExplorerModal
                open={explorerOpen}
                onClose={() => setExplorerOpen(false)}
                onPickCity={(city) => handleSend(`Plan a trip to ${city}`)}
            />
        </div>
    );
}

function EmptyHero({ onSend, sending, assistantReplyTick = 0 }) {
    const { t } = useTranslation();
    const { geo, userGeo } = useGeo();
    const country = (userGeo?.country_code || geo?.country_code || "DEFAULT").toUpperCase();
    const state = (userGeo?.region || geo?.region || geo?.region_code || "").toString().slice(0, 6).toUpperCase();
    const month = new Date().getMonth() + 1;

    // Phase 8M.1 — dynamic location-aware suggestions. Hits
    // /api/suggestions on mount and whenever the detected country
    // changes. Falls back to the static i18n trio if the call fails
    // so the row never goes empty.
    const staticFallback = [
        t("chat.suggested_prompt_flight"),
        t("chat.suggested_prompt_hotel"),
        t("chat.suggested_prompt_things_to_do"),
    ];
    const [suggestions, setSuggestions] = useState(staticFallback);

    useEffect(() => {
        let cancelled = false;
        (async () => {
            try {
                const params = { country, month };
                if (state) params.state = state;
                const res = await axios.get(`${API}/suggestions`, {
                    params,
                    timeout: 8000,
                });
                if (!cancelled && Array.isArray(res.data?.items) && res.data.items.length) {
                    setSuggestions(res.data.items.slice(0, 4));
                }
            } catch {
                /* keep static fallback */
            }
        })();
        return () => {
            cancelled = true;
        };
    }, [country, state, month]);

    return (
        <div
            className="relative flex flex-1 overflow-hidden"
            data-testid="empty-hero"
        >
            <div className="relative mx-auto flex w-full max-w-[820px] flex-col px-5">
                <div className="flex flex-1 flex-col items-center justify-end pb-8">
                    <div className="w-full pt-[12vh] text-center">
                        <h1 className="text-4xl font-medium tracking-tight sm:text-5xl">
                            {(() => {
                                const heading = t("chat.where_to_next");
                                const m = heading.match(/^([\s\S]*?)([?？])\s*$/u);
                                if (!m) return heading;
                                return (
                                    <>
                                        {m[1]}
                                        <span className="qmark-with-dots" data-testid="qmark-with-dots">
                                            {m[2]}
                                        </span>
                                    </>
                                );
                            })()}
                        </h1>
                        <p className="mx-auto mt-4 max-w-md text-[15px] text-[#737373]">
                            {t("chat.hero_subtitle")}
                        </p>
                    </div>
                </div>

                <div className="pb-[18vh]" data-testid="hero-composer-wrap">
                    <div className="mx-auto mb-3 w-full max-w-2xl space-y-2 px-1">
                        <DailyPickBanner
                            originIata={userGeo?.nearest_iata}
                            currency={(geo?.currency || "USD").toUpperCase()}
                            onPick={onSend}
                        />
                        <WatchStrip onPick={onSend} />
                        <RecentChips onPick={onSend} />
                    </div>
                    <NearMeChip />
                    <ChatComposer
                        disabled={sending}
                        onSend={onSend}
                        assistantReplyTick={assistantReplyTick}
                    />
                    <AiDisclaimer />
                    <div className="mt-4 flex flex-wrap items-center justify-center gap-2">
                        {suggestions.map((s, i) => (
                            <button
                                key={i}
                                type="button"
                                onClick={() => onSend(s)}
                                data-testid={`suggestion-${i}`}
                                className="group inline-flex items-center gap-1.5 rounded-full border border-[#E5E5E5] bg-white px-2.5 py-1 text-[10.5px] text-[#0A0A0A] transition hover:border-[#0A0A0A] hover:bg-[#F5F5F5]"
                            >
                                <span className="inline-block h-1 w-1 rounded-full bg-[#2563EB]" />
                                {s}
                            </button>
                        ))}
                    </div>
                    <div
                        className="mt-5 flex items-center justify-center gap-3"
                        data-testid="hero-app-badges"
                    >
                        <a
                            href="https://apps.apple.com/us/app/piqpath/id6772143338"
                            target="_blank"
                            rel="noopener noreferrer"
                            data-testid="badge-app-store"
                            aria-label="Download Piqpath on the App Store"
                            className="inline-block transition hover:opacity-80"
                        >
                            <img
                                src="/badges/app-store.svg"
                                alt="Download on the App Store"
                                className="h-9 w-auto"
                            />
                        </a>
                        <a
                            href="https://play.google.com/store/apps/details?id=com.piqpath.app"
                            target="_blank"
                            rel="noopener noreferrer"
                            data-testid="badge-play-store"
                            aria-label="Get Piqpath on Google Play"
                            className="inline-block transition hover:opacity-80"
                        >
                            <img
                                src="/badges/play-store.svg"
                                alt="Get it on Google Play"
                                className="h-9 w-auto"
                            />
                        </a>
                    </div>

                    {/* Phase 20 — below-the-fold: crawlable value prop +
                        how-it-works. First-timers and search engines
                        scroll; everyone else has already started typing. */}
                    <section
                        className="mx-auto mt-16 w-full max-w-3xl px-4 pb-10 text-center"
                        data-testid="hero-below-fold"
                    >
                        <p className="text-[13px] font-medium text-[#525252]">
                            AI travel search with real fares, not guesses — flights,
                            hotels, and holiday packages compared live in one chat.
                        </p>
                        <div className="mt-8 grid grid-cols-1 gap-6 sm:grid-cols-3" data-testid="how-it-works">
                            {[
                                ["1", "Ask in plain English", "\u201cGoa for a long weekend under \u20b915k\u201d works. No forms, no filters."],
                                ["2", "Compare live prices", "Real flight, hotel, and package fares side by side \u2014 sortable, watchable."],
                                ["3", "Book on the provider\u2019s site", "One tap takes you to the airline, hotel, or travel partner\u2019s site to complete your booking."],
                            ].map(([n, title, body]) => (
                                <div key={n} className="rounded-xl border border-[#E5E5E5] bg-white p-4 text-left">
                                    <div className="text-[11px] font-semibold tracking-[0.18em] text-[#A3A3A3]">STEP {n}</div>
                                    <div className="mt-1 text-[14px] font-semibold text-[#0A0A0A]">{title}</div>
                                    <p className="mt-1 text-[12.5px] leading-relaxed text-[#525252]">{body}</p>
                                </div>
                            ))}
                        </div>
                        <nav className="mt-8 flex items-center justify-center gap-5 text-[12px] font-medium text-[#525252]" data-testid="hero-footer-links">
                            <a href="/blog" className="transition hover:text-[#0A0A0A]">Travel guides</a>
                            <a href="/faq" className="transition hover:text-[#0A0A0A]">FAQ</a>
                            <a href="/about" className="transition hover:text-[#0A0A0A]">About</a>
                            <a href="/privacy" className="transition hover:text-[#0A0A0A]">Privacy</a>
                            <a href="/partner" className="transition hover:text-[#0A0A0A]">Partner with us</a>
                        </nav>
                    </section>
                </div>
            </div>

            <div className="pointer-events-none absolute right-6 top-[14vh] hidden lg:block">
                <div className="pointer-events-auto">
                    <DealsRail onPick={onSend} />
                </div>
            </div>
        </div>
    );
}
